import { useRef, useState } from "react";
import type { BrokerOutreachStateDto } from "@/api/brokerOutreach";
import type { BuyerSubmissionDetailDto } from "@/api/buyers";
import { Skeleton } from "@/design-system/components/ui";
import { useSendHistory } from "@/hooks/buyers/useSendHistory";
import { useAuth } from "@/hooks/useAuth";
import { DetailSection } from "../detailBits";
import { InlineQueryError } from "../queryStates";
import { BrokerFollowUpEmailCard } from "../brokerIntro/BrokerFollowUpEmailCard";
import { BrokerIntroEmailPanel } from "../brokerIntro/BrokerIntroEmailPanel";
import { BrokerOutreachWordingPanel } from "../brokerIntro/BrokerOutreachWordingPanel";
import { BrokerTextCard } from "../brokerIntro/BrokerTextCard";
import { BrokerWebFormCard } from "../brokerIntro/BrokerWebFormCard";
import { ProgressStrip } from "../brokerIntro/ProgressStrip";
import { ReadinessStrip } from "../brokerIntro/ReadinessStrip";
import { useBrokerOutreachState } from "../brokerIntro/useBrokerOutreach";
import { CollapsibleCard } from "../introCards/CollapsibleCard";
import { introCardCopy } from "../introCards/introCardCopy";
import {
  firstOpenCard,
  isDone,
  nextOpenCard,
  OUTREACH_ORDER,
  outreachCardStatus,
  type OutreachCardKey,
} from "../introCards/introCardStatus";
import { ExpandAllControl } from "../introCards/SentFold";
import { SendHistory } from "../sendHistory/SendHistory";

/**
 * The Broker intro tab (D23–D33) — everything that reaches the LISTING BROKER
 * before any package.
 *
 * <p><b>In order (D25):</b> the readiness strip (D24) — can we reach them, and
 * by what — then 1 Email, 2 Text, 3 Web form, then the follow-ups. No card is
 * locked by another: a card that cannot be used says why, as text.</p>
 *
 * <p>🔴 <b>Phoenix sends only the emails.</b> The text and the web form are
 * copied by the consultant, sent from their own phone or the broker's form, and
 * LOGGED; each log is one send version and one communications entry on the
 * buyer, the submission and the listing, and moves nothing on the buyer — no
 * contact clock, no status, no task (D32).</p>
 *
 * <p>🔴 <b>Written as the buyer's consultant (D30).</b> The text and web form
 * speak as the consultant; when somebody else is at the keyboard the state says
 * so, and the note is shown above the cards rather than inside one, because it
 * applies to all of them.</p>
 *
 * <p>`canWrite` arrives from the page, as on every other tab — the module
 * permission AND the row's own ownership answer — and no card computes its
 * own.</p>
 *
 * <p><b>Collapsible cards (D2–D8, J1–J8).</b> This tab owns which card is open.
 * On first load the first USABLE card in outreach order that is not sent or
 * logged opens and every other card starts collapsed (D4, J6); if the outreach
 * read fails the email card opens. After a send, record or log taken HERE, that
 * card folds and the next not-done card opens (J6) — a refetch (window focus,
 * another browser tab) moves the pills and the progress strip but never opens
 * or closes a card. Nothing is persisted: leaving the tab unmounts it and
 * re-opening re-applies the default. Every status comes from `introCardStatus`.</p>
 */
type CardKey = OutreachCardKey | "history";
type OpenMap = Record<CardKey, boolean>;

const ALL_CLOSED: OpenMap = {
  email: false,
  text: false,
  webForm: false,
  followUpEmail: false,
  followUpText: false,
  history: false,
};

function outreachCards(data: BrokerOutreachStateDto) {
  return OUTREACH_ORDER.map((key) => ({
    key,
    status: outreachCardStatus(data[key]),
    available: data[key].available,
  }));
}

export function SubmissionBrokerIntroTab({
  s,
  canWrite,
}: {
  s: BuyerSubmissionDetailDto;
  canWrite: boolean;
}) {
  const { hasPermission } = useAuth();
  const state = useBrokerOutreachState(s.id);
  const history = useSendHistory(s.id);
  const cardRefs = useRef<Partial<Record<OutreachCardKey, HTMLDivElement | null>>>({});

  // D4 / J6 — the default is decided ONCE, when the first answer lands (or
  // fails), never again on a refetch.
  const [open, setOpen] = useState<OpenMap | null>(null);
  const [actedOn, setActedOn] = useState<OutreachCardKey | null>(null);

  const cards = state.data ? outreachCards(state.data) : null;

  if (open === null && (cards || state.isError)) {
    const first = cards ? firstOpenCard(cards) : "email";
    setOpen(first ? { ...ALL_CLOSED, [first]: true } : ALL_CLOSED);
  }

  // J6 — collapse-and-advance, only after an action taken in this tab, and
  // only once the card it was taken on reads as done.
  if (open && actedOn && cards) {
    const acted = cards.find((c) => c.key === actedOn);
    if (acted && isDone(acted.status)) {
      const next = nextOpenCard(cards, actedOn);
      setOpen({ ...open, [actedOn]: false, ...(next ? { [next]: true } : {}) });
      setActedOn(null);
    }
  }

  const isOpen = (key: CardKey) => open?.[key] ?? false;
  const setCard = (key: CardKey) => (next: boolean) =>
    setOpen((prev) => ({ ...(prev ?? ALL_CLOSED), [key]: next }));
  // Without the outreach read there is no status to wait for: fold at once.
  const acted = (key: OutreachCardKey) => () =>
    state.data ? setActedOn(key) : setOpen((prev) => ({ ...(prev ?? ALL_CLOSED), [key]: false }));
  const setAll = (value: boolean) =>
    setOpen({
      email: value,
      text: value,
      webForm: value,
      followUpEmail: value,
      followUpText: value,
      history: value,
    });
  // The cards actually on screen: only the email card without the outreach read.
  const shownKeys: CardKey[] = state.data ? [...OUTREACH_ORDER, "history"] : ["email", "history"];
  const anyOpen = shownKeys.some(isOpen);

  const selectStep = (key: OutreachCardKey) => {
    setCard(key)(true);
    cardRefs.current[key]?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };

  const h = history.data;
  const historyCount =
    (h?.brokerIntros.length ?? 0) +
    (h?.brokerTexts?.length ?? 0) +
    (h?.brokerWebForms?.length ?? 0) +
    (h?.brokerFollowUpEmails?.length ?? 0) +
    (h?.brokerFollowUpTexts?.length ?? 0);

  return (
    <div className="grid gap-space-5" data-testid="broker-intro-tab">
      <ExpandAllControl
        anyOpen={anyOpen}
        onExpandAll={() => setAll(true)}
        onCollapseAll={() => setAll(false)}
        testId="broker-intro-expand-all"
      />

      {state.isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : state.isError || !state.data ? (
        <InlineQueryError
          what="the listing broker's details"
          onRetry={() => void state.refetch()}
          retrying={state.isFetching}
        />
      ) : (
        <>
          <ReadinessStrip readiness={state.data.readiness} />
          {cards ? <ProgressStrip steps={cards} onSelect={selectStep} /> : null}
          {state.data.sender.note ? (
            <p className="text-small text-status-warning-ink m-0" data-testid="broker-intro-written-as">
              {state.data.sender.note}
            </p>
          ) : null}
        </>
      )}

      {/* 1 · Email — today's broker intro email (D26), with its own read. */}
      <div ref={(el) => void (cardRefs.current.email = el)}>
        <BrokerIntroEmailPanel
          submissionId={s.id}
          canWrite={canWrite}
          open={isOpen("email")}
          onOpenChange={setCard("email")}
          outreachCard={state.data?.email ?? null}
          onActed={acted("email")}
        />
      </div>

      {state.data ? (
        <>
          {/* 2 · Text */}
          <div ref={(el) => void (cardRefs.current.text = el)}>
            <BrokerTextCard
              submissionId={s.id}
              channel="text"
              title="Text"
              card={state.data.text}
              phone={state.data.readiness.agentPhone}
              targetCharacters={state.data.textTargetCharacters}
              canWrite={canWrite}
              testId="broker-text-card"
              open={isOpen("text")}
              onOpenChange={setCard("text")}
              onActed={acted("text")}
            />
          </div>

          {/* 3 · Web form */}
          <div ref={(el) => void (cardRefs.current.webForm = el)}>
            <BrokerWebFormCard
              submissionId={s.id}
              state={state.data}
              canWrite={canWrite}
              canEditListing={hasPermission("Listings.Manage")}
              open={isOpen("webForm")}
              onOpenChange={setCard("webForm")}
              onActed={acted("webForm")}
            />
          </div>

          {/* Follow-ups (D33) — drafted only on request, each available only
              once the first message of its channel has gone. */}
          <section className="grid gap-space-3" aria-label="Follow-ups">
            <h3 className="text-body font-semibold m-0">Follow-ups</h3>
            <div ref={(el) => void (cardRefs.current.followUpEmail = el)}>
              <BrokerFollowUpEmailCard
                submissionId={s.id}
                card={state.data.followUpEmail}
                agentEmail={state.data.readiness.agentEmail}
                maxWords={state.data.followUpEmailMaxWords}
                canWrite={canWrite}
                open={isOpen("followUpEmail")}
                onOpenChange={setCard("followUpEmail")}
                onActed={acted("followUpEmail")}
              />
            </div>
            <div ref={(el) => void (cardRefs.current.followUpText = el)}>
              <BrokerTextCard
                submissionId={s.id}
                channel="follow-up-text"
                title="Follow-up text"
                card={state.data.followUpText}
                phone={state.data.readiness.agentPhone}
                targetCharacters={state.data.textTargetCharacters}
                canWrite={canWrite}
                testId="broker-followup-text-card"
                open={isOpen("followUpText")}
                onOpenChange={setCard("followUpText")}
                onActed={acted("followUpText")}
              />
            </div>
          </section>

          {state.data.canEditWording ? <BrokerOutreachWordingPanel /> : null}
        </>
      ) : null}

      {/* J8 — the history, unchanged inside, folded into one row. */}
      <CollapsibleCard
        open={isOpen("history")}
        onOpenChange={setCard("history")}
        title={introCardCopy.sendHistory(historyCount)}
        testId="broker-send-history"
      >
        <DetailSection title="Sent to the listing broker">
          <div className="grid gap-space-5">
            <HistoryGroup title="Emails">
              <SendHistory submissionId={s.id} kind="BrokerIntro" canWrite={canWrite} />
            </HistoryGroup>
            <HistoryGroup title="Texts">
              <SendHistory submissionId={s.id} kind="BrokerText" canWrite={canWrite} />
            </HistoryGroup>
            <HistoryGroup title="Web form">
              <SendHistory submissionId={s.id} kind="BrokerWebForm" canWrite={canWrite} />
            </HistoryGroup>
            <HistoryGroup title="Follow-up emails">
              <SendHistory submissionId={s.id} kind="BrokerEmailFU" canWrite={canWrite} />
            </HistoryGroup>
            <HistoryGroup title="Follow-up texts">
              <SendHistory submissionId={s.id} kind="BrokerTextFU" canWrite={canWrite} />
            </HistoryGroup>
          </div>
        </DetailSection>
      </CollapsibleCard>
    </div>
  );
}

function HistoryGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-space-2">
      <h4 className="text-small font-medium m-0">{title}</h4>
      {children}
    </div>
  );
}
